import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { businessApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Building2, Key, Eye, EyeOff, Download, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Businesses = () => {
  const { canManageBusinesses, token } = useAuthStore();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showApiToken, setShowApiToken] = useState({});
  
  // Migration state
  const [migrating, setMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    api_token: '',
    is_active: true,
  });

  const fetchData = async () => {
    try {
      const response = await businessApi.getAll();
      setBusinesses(response.data);
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (business = null) => {
    if (business) {
      setSelectedBusiness(business);
      setFormData({
        name: business.name,
        api_token: business.api_token === '***hidden***' ? '' : business.api_token,
        is_active: business.is_active,
      });
    } else {
      setSelectedBusiness(null);
      setFormData({
        name: '',
        api_token: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter a business name');
      return;
    }

    if (!selectedBusiness && !formData.api_token) {
      toast.error('API token is required for new businesses');
      return;
    }

    setSaving(true);
    try {
      if (selectedBusiness) {
        const updateData = { name: formData.name, is_active: formData.is_active };
        if (formData.api_token) {
          updateData.api_token = formData.api_token;
        }
        await businessApi.update(selectedBusiness.id, updateData);
        toast.success('Business updated successfully');
      } else {
        await businessApi.create(formData);
        toast.success('Business created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBusiness) return;
    
    try {
      await businessApi.delete(selectedBusiness.id);
      toast.success('Business deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedBusiness(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete business';
      toast.error(message);
    }
  };

  const openDeleteDialog = (business) => {
    setSelectedBusiness(business);
    setDeleteDialogOpen(true);
  };

  const toggleApiTokenVisibility = (businessId) => {
    setShowApiToken((prev) => ({
      ...prev,
      [businessId]: !prev[businessId],
    }));
  };

  // Migration functions
  const startMigration = async () => {
    try {
      setMigrating(true);
      const response = await axios.post(
        `${API_URL}/api/migration/pull-from-preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Migration started! This may take 30-60 minutes.');
      checkMigrationStatus();
    } catch (error) {
      toast.error('Failed to start migration');
      setMigrating(false);
    }
  };

  const checkMigrationStatus = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/migration/pull-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMigrationStatus(response.data);
      
      if (response.data.in_progress) {
        // Poll every 5 seconds
        setTimeout(checkMigrationStatus, 5000);
      } else if (response.data.completed) {
        toast.success(`Migration complete! ${response.data.migrated.toLocaleString()} receipts imported.`);
        setMigrating(false);
        fetchData();
      } else if (response.data.error) {
        toast.error(`Migration error: ${response.data.error}`);
        setMigrating(false);
      }
    } catch (error) {
      console.error('Failed to check migration status:', error);
    }
  };

  // Check migration status on load
  useEffect(() => {
    const checkInitialStatus = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/migration/pull-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.in_progress) {
          setMigrating(true);
          setMigrationStatus(response.data);
          checkMigrationStatus();
        }
      } catch (error) {
        // Ignore - endpoint may not exist on older deployments
      }
    };
    if (token) checkInitialStatus();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-coffee-espresso">
            Business Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage integrated businesses and their Loyverse API tokens
          </p>
        </div>
        {canManageBusinesses() && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-business-btn" className="rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Add Business
          </Button>
        )}
      </div>

      {/* Data Migration Card */}
      {canManageBusinesses() && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Download className="w-5 h-5" />
              Data Migration from Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-blue-700 mb-4">
              Pull all receipt data from the preview environment to this deployed app.
              This will import ~525,000 receipts and may take 30-60 minutes.
            </p>
            
            {migrationStatus && migrationStatus.in_progress && (
              <div className="mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress: {migrationStatus.migrated?.toLocaleString()} / {migrationStatus.total?.toLocaleString()}</span>
                  <span>{migrationStatus.total > 0 ? Math.round((migrationStatus.migrated / migrationStatus.total) * 100) : 0}%</span>
                </div>
                <Progress value={migrationStatus.total > 0 ? (migrationStatus.migrated / migrationStatus.total) * 100 : 0} className="h-2" />
                <p className="text-xs text-muted-foreground">Batch {migrationStatus.current_batch}</p>
              </div>
            )}
            
            {migrationStatus && migrationStatus.completed && (
              <div className="mb-4 p-3 bg-green-100 rounded-lg flex items-center gap-2 text-green-800">
                <CheckCircle className="w-5 h-5" />
                Migration completed! {migrationStatus.migrated?.toLocaleString()} receipts imported.
              </div>
            )}
            
            {migrationStatus && migrationStatus.error && (
              <div className="mb-4 p-3 bg-red-100 rounded-lg text-red-800">
                Error: {migrationStatus.error}
              </div>
            )}
            
            <Button 
              onClick={startMigration} 
              disabled={migrating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {migrating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Start Migration
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Businesses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Businesses ({businesses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>API Token</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businesses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No businesses found
                    </TableCell>
                  </TableRow>
                ) : (
                  businesses.map((business) => (
                    <TableRow key={business.id}>
                      <TableCell className="font-medium">{business.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                            {business.api_token === '***hidden***'
                              ? '***hidden***'
                              : showApiToken[business.id]
                              ? business.api_token
                              : `${business.api_token?.substring(0, 8)}...`}
                          </code>
                          {business.api_token !== '***hidden***' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleApiTokenVisibility(business.id)}
                              className="h-8 w-8"
                            >
                              {showApiToken[business.id] ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={business.is_active ? 'default' : 'secondary'}
                          className={business.is_active ? 'bg-green-100 text-green-700' : ''}
                        >
                          {business.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageBusinesses() && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(business)}
                              data-testid={`edit-business-${business.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(business)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-business-${business.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* API Token Info */}
      <Card className="bg-muted/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Loyverse API Tokens</h3>
              <p className="text-sm text-muted-foreground">
                To get your Loyverse API token, go to your Loyverse Back Office → Settings → Access tokens → 
                Create new token. Make sure to enable the necessary permissions for receipts, items, employees, 
                stores, and other data you want to access.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedBusiness ? 'Edit Business' : 'Add New Business'}</DialogTitle>
            <DialogDescription>
              {selectedBusiness ? 'Update business details' : 'Add a new Loyverse integrated business'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Business Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Coffee Shop"
                data-testid="business-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="api_token">
                Loyverse API Token {selectedBusiness ? '(leave empty to keep current)' : '*'}
              </Label>
              <Input
                id="api_token"
                type="password"
                value={formData.api_token}
                onChange={(e) => setFormData({ ...formData, api_token: e.target.value })}
                placeholder="Enter API token"
                data-testid="business-api-token-input"
              />
            </div>

            {selectedBusiness && (
              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Active Status</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="business-active-switch"
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-business-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Business</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedBusiness?.name}"? This will remove all associated data and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-business"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Businesses;
